import { useMemo } from 'react';
import type { HierarchyNode } from '@bower/shared/types/api';
import { useIsReadOnly } from '../../hooks/user/useWorkspaceRole';
import { useModalCreateOpeners } from '../../hooks/create/useModalCreateOpeners';
import { CreateHereMenuItem } from '../features/create/CreateHereMenuItem';
import { collectionDestination } from '../features/create/destination/createDestination';

interface TreeNodeCreateSubmenuProps {
  node: HierarchyNode;
  /** Analytics label for the surface the menu was opened from. */
  surface?: string;
  /**
   * After the modal makes a collection here. Omitted, the modal opens the new
   * collection. Pass a stable function: it is a dependency of the openers.
   */
  onCollectionCreated?: (collection: { readonly id: string }) => void;
}

/**
 * TreeNodeCreateSubmenu: the "Create in…" item in a collection's menu, opening
 * the one Create modal aimed at that collection.
 *
 * A component (rather than a prop on the menu) because the openers come from
 * a hook and must be bound per node: the parent renders many nodes and cannot
 * call the hook in a loop.
 */
export function TreeNodeCreateSubmenu({
  node,
  surface = 'sidebar_tree_kebab',
  onCollectionCreated,
}: TreeNodeCreateSubmenuProps) {
  // Viewers can't create at all, so a GUEST never sees an item whose every
  // create would 403.
  const isReadOnlyRole = useIsReadOnly();
  const here = useMemo(
    () =>
      node.type === 'project' ? collectionDestination({ id: node.id, name: node.name }) : undefined,
    [node.type, node.id, node.name],
  );
  const modal = useModalCreateOpeners(here, surface, { onCollectionCreated });

  // COLLECTIONS ONLY. On an artifact the only thing that can nest is an
  // attachment, which is not a question worth a menu item there.
  //
  // ACL: `useIsReadOnly` is the WORKSPACE-ROLE gate, and that is all this
  // check is. Hiding a menu item is not enforcement: the real gate is
  // server-side, in `createArtifact`, which asserts entity-level EDIT on the
  // parent collection as well as workspace-level write.
  //
  // That assert had to go in the SERVICE, not the route: `POST /artifacts`
  // carries its parent in the body, and `requireEntityAccess` keys off a route
  // param, so the router-walking guard-inventory test cannot see this route at
  // all. Before it existed, a MEMBER with workspace EDIT but only VIEW on a
  // RESTRICTED collection could create into it.
  if (isReadOnlyRole || node.type !== 'project') return null;
  return <CreateHereMenuItem scopeName={node.name} onClick={modal.openChoose} />;
}
